import { SelectedItemsObj } from '@components/multi-selector/types';
import { FEATURE_FLAGS } from '@constants/feature';
import UserPermissionTypes from '@constants/permissions';
import { roleDisplayNameMap } from '@features/team-management/constants';
import { getPermissionCatalogEntry } from '@features/team-management/constants/permissionCatalog';
import {
  type CreateRoleFeatureContext,
  filterDisplayablePermissionSlugs
} from '@features/team-management/constants/permissionFeatureGates';
import {
  collapsePermissionsForUi,
  isAutoIncludedHiddenPermission,
  isPermissionEligibleForCustomRoleUi,
  sortPermissionsByUiOrder,
  stripOmittedCustomRolePermissions
} from '@features/team-management/constants/permissionUiConfig';
import { getScopeUiVariant } from '@features/team-management/constants/roleScopeUi';
import { isNameAndPhoneValid } from '@features/team-management/helper';
import {
  CreateCustomRoleFormState,
  CreateCustomRoleResponse,
  GetCustomRolesResponse,
  InviteBranchPayload,
  InviteGroupPayload,
  InviteMemberBody,
  MemberFormState,
  OrgRoleMeta,
  PermissionRule,
  PermissionRuleType,
  RoleScope,
  UpdateMemberRoleBody
} from '@features/team-management/types/customRoles';
import {
  capitalizeFirstLetter,
  capitalizeWords,
  pluralize
} from '@utils/stringFormat';

export const DUPLICATE_ROLE_NAME_ERROR = 'Role name already exists';
export const MANDATORY_PERMISSION_TOOLTIP =
  'This permission is mandatory for this role';
export const CUSTOM_ROLE_NAME_MIN_LENGTH = 2;
export const CUSTOM_ROLE_NAME_MAX_LENGTH = 30;
export const CUSTOM_ROLE_NAME_MIN_ERROR = `Role name must be at least ${CUSTOM_ROLE_NAME_MIN_LENGTH} characters`;
export const CUSTOM_ROLE_NAME_MAX_ERROR = `Role name must not exceed ${CUSTOM_ROLE_NAME_MAX_LENGTH} characters`;

/**
 * Split permissions that replaced the legacy `payment_order_edit` umbrella.
 * Kept only so older mobile app builds that still check `payment_order_edit`
 * keep working when a custom role grants the full action set.
 * Remove this compat layer (and stop sending `payment_order_edit`) once those
 * clients are force-updated / retired.
 */
export const PAYMENT_ORDER_EDIT_COMPAT_PERMISSIONS: string[] = [
  UserPermissionTypes.PAYMENT_ORDER_DISABLE,
  UserPermissionTypes.PAYMENT_ORDER_MARK_PAID,
  UserPermissionTypes.PAYMENT_ORDER_SPLIT_ENABLE,
  UserPermissionTypes.PAYMENT_ORDER_CLOSE,
  UserPermissionTypes.PAYMENT_ORDER_DISCOUNT_CREATE
];

export const stripLegacyPaymentOrderEdit = (permissions: string[]): string[] =>
  permissions.filter((slug) => slug !== UserPermissionTypes.PAYMENT_ORDER_EDIT);

/** True when every assignable split payment-action permission is selected. */
export const shouldIncludeLegacyPaymentOrderEdit = (
  selectedPermissions: string[],
  assignablePermissions: string[]
): boolean => {
  const required = PAYMENT_ORDER_EDIT_COMPAT_PERMISSIONS.filter((slug) =>
    assignablePermissions.includes(slug)
  );
  if (required.length === 0) return false;
  return required.every((slug) => selectedPermissions.includes(slug));
};

export const createInitialCreateCustomRoleFormState =
  (): CreateCustomRoleFormState => ({
    roleName: '',
    baseRoleId: null,
    selectedPermissions: [],
    roleNameError: '',
    permissionSelectionError: ''
  });

export const isDuplicateRoleName = (
  roleName: string,
  existingRoleNames: string[]
): boolean =>
  existingRoleNames.some(
    (name) => name.toLowerCase() === roleName.trim().toLowerCase()
  );

export const getCustomRoleNameError = (
  roleName: string,
  existingRoleNames: string[] = []
): string => {
  const trimmed = roleName.trim();
  if (!trimmed) return '';
  if (trimmed.length < CUSTOM_ROLE_NAME_MIN_LENGTH) {
    return CUSTOM_ROLE_NAME_MIN_ERROR;
  }
  if (trimmed.length > CUSTOM_ROLE_NAME_MAX_LENGTH) {
    return CUSTOM_ROLE_NAME_MAX_ERROR;
  }
  if (isDuplicateRoleName(trimmed, existingRoleNames)) {
    return DUPLICATE_ROLE_NAME_ERROR;
  }
  return '';
};

export const mapCreateCustomRoleResponseToOrgRoleMeta = (
  response: CreateCustomRoleResponse
): OrgRoleMeta => ({
  id: response.id,
  name: response.name,
  displayName: response.display_name,
  role_scope: response.role_scope,
  permissions: response.permissions,
  isCustom: true,
  allowExtendingRole: true
});

export const getCreateFlowPermissionSlugs = (
  baseRole: OrgRoleMeta,
  currentUserPermissions: string[]
): string[] =>
  baseRole.permissions.filter((slug) => currentUserPermissions.includes(slug));

export const getPermissionsByRule = (
  rules: PermissionRule[] | undefined,
  rule: PermissionRuleType
): string[] =>
  (rules ?? [])
    .filter((item) => item.rule === rule)
    .flatMap((item) => item.permissions);

/** Slugs the API marks HIDE: hidden in UI but still granted when assignable. */
const getHideRuleSlugs = (rules: PermissionRule[] | undefined): Set<string> =>
  new Set(getPermissionsByRule(rules, PermissionRuleType.HIDE));

export const getOrPermissionGroups = (
  rules: PermissionRule[] | undefined
): string[][] =>
  (rules ?? [])
    .filter((item) => item.rule === PermissionRuleType.OR)
    .map((item) => item.permissions);

/** Single-permission OR groups behave like MANDATORY (locked). */
export const getSingleOrLockedPermissions = (
  rules: PermissionRule[] | undefined
): string[] =>
  getOrPermissionGroups(rules)
    .filter((group) => group.length === 1)
    .map((group) => group[0]);

export const getLockedCreateFlowPermissions = (
  baseRole: OrgRoleMeta,
  assignablePermissions: string[]
): string[] => {
  const locked = new Set([
    ...getPermissionsByRule(
      baseRole.permissionRules,
      PermissionRuleType.MANDATORY
    ),
    ...getSingleOrLockedPermissions(baseRole.permissionRules)
  ]);
  return assignablePermissions.filter((slug) => locked.has(slug));
};

/**
 * Permissions auto-included on create (not shown as checkboxes):
 * - API HIDE rules
 * - Feature-flag / attendance gated modules
 * - branch_view when assignable (hidden in UI, still passed)
 *
 * payment_order_edit is never auto-included here — see
 * buildCreateCustomRolePermissionsPayload (AND of split actions).
 */
export const getHiddenCreateFlowPermissions = (
  baseRole: OrgRoleMeta | null | undefined,
  assignablePermissions: string[],
  featureContext: CreateRoleFeatureContext
): string[] => {
  const hideByRule = getHideRuleSlugs(baseRole?.permissionRules);
  const withoutHideRules = assignablePermissions.filter(
    (slug) => !hideByRule.has(slug)
  );
  const featureDisplayable = filterDisplayablePermissionSlugs(
    withoutHideRules,
    featureContext
  );
  const featureOrRuleHidden = assignablePermissions.filter(
    (slug) =>
      !featureDisplayable.includes(slug) &&
      slug !== UserPermissionTypes.PAYMENT_ORDER_EDIT
  );

  const branchViewIncluded = assignablePermissions.filter((slug) =>
    isAutoIncludedHiddenPermission(slug)
  );

  return [...new Set([...featureOrRuleHidden, ...branchViewIncluded])];
};

export const getDisplayableCreateFlowPermissions = (
  baseRole: OrgRoleMeta | null | undefined,
  assignablePermissions: string[],
  featureContext: CreateRoleFeatureContext
): string[] => {
  const autoIncluded = new Set(
    getHiddenCreateFlowPermissions(
      baseRole,
      assignablePermissions,
      featureContext
    )
  );

  const eligible = assignablePermissions.filter(
    (slug) =>
      !autoIncluded.has(slug) &&
      slug !== UserPermissionTypes.PAYMENT_ORDER_EDIT &&
      isPermissionEligibleForCustomRoleUi(slug)
  );

  return sortPermissionsByUiOrder(collapsePermissionsForUi(eligible));
};

/**
 * Multi-permission OR groups with nothing selected.
 * Single-permission OR groups are locked instead (see getSingleOrLockedPermissions).
 * When assignablePermissions is provided, only assignable members count — OR
 * groups the user cannot grant are ignored so Create is not stuck disabled.
 */
export const getUnsatisfiedMultiOrPermissionGroups = (
  selectedPermissions: string[],
  rules: PermissionRule[] | undefined,
  assignablePermissions?: string[]
): string[][] =>
  getOrPermissionGroups(rules).filter((group) => {
    if (group.length <= 1) return false;
    const relevantGroup =
      assignablePermissions && assignablePermissions.length > 0
        ? group.filter((permission) =>
            assignablePermissions.includes(permission)
          )
        : group;
    if (relevantGroup.length === 0) return false;
    return !relevantGroup.some((permission) =>
      selectedPermissions.includes(permission)
    );
  });

/**
 * When only one permission remains selected in a multi-OR group,
 * that last permission is disabled so it cannot be unchecked.
 */
export const getLastRemainingOrPermissionSlugs = (
  selectedPermissions: string[],
  rules: PermissionRule[] | undefined
): string[] => {
  const locked: string[] = [];
  getOrPermissionGroups(rules).forEach((group) => {
    if (group.length <= 1) return;
    const selectedInGroup = group.filter((permission) =>
      selectedPermissions.includes(permission)
    );
    if (selectedInGroup.length === 1) {
      locked.push(selectedInGroup[0]);
    }
  });
  return locked;
};

/** e.g. At least one of "A," "B," or "C" must stay enabled. */
export const formatOrPermissionStayEnabledTooltip = (
  groupSlugs: string[]
): string => {
  if (!groupSlugs.length) return '';

  const quotedLabels = groupSlugs.map((slug, index) => {
    const { label } = getPermissionCatalogEntry(slug);
    const isLast = index === groupSlugs.length - 1;
    return isLast ? `"${label}"` : `"${label},"`;
  });

  if (quotedLabels.length === 1) {
    return `At least one of ${quotedLabels[0]} must stay enabled.`;
  }

  const leading = quotedLabels.slice(0, -1).join(' ');
  const last = quotedLabels[quotedLabels.length - 1];
  return `At least one of ${leading} or ${last} must stay enabled.`;
};

/** Tooltip copy keyed by the last remaining permission slug in each OR group. */
export const getOrPermissionTooltipBySlug = (
  selectedPermissions: string[],
  rules: PermissionRule[] | undefined
): Record<string, string> => {
  const tooltips: Record<string, string> = {};
  getOrPermissionGroups(rules).forEach((group) => {
    if (group.length <= 1) return;
    const selectedInGroup = group.filter((permission) =>
      selectedPermissions.includes(permission)
    );
    if (selectedInGroup.length === 1) {
      const [remainingSlug] = selectedInGroup;
      tooltips[remainingSlug] = formatOrPermissionStayEnabledTooltip(group);
    }
  });
  return tooltips;
};

/** Keep at least one permission from each multi-OR group after bulk unchecks. */
export const preserveOrPermissionSelections = (
  nextSelected: string[],
  previousSelected: string[],
  rules: PermissionRule[] | undefined
): string[] => {
  const result = new Set(nextSelected);
  getOrPermissionGroups(rules).forEach((group) => {
    if (group.length <= 1) return;
    const hasAny = group.some((permission) => result.has(permission));
    if (hasAny) return;

    const previouslySelected = [...group]
      .reverse()
      .find((permission) => previousSelected.includes(permission));
    if (previouslySelected) result.add(previouslySelected);
  });
  return [...result];
};

/**
 * True only when `slugToRemove` is the last remaining selection in its
 * multi-OR group. Pre-existing unsatisfied OR groups must not block other
 * permission unchecks.
 */
export const wouldViolateOrPermissionRule = (
  selectedPermissions: string[],
  slugToRemove: string,
  rules: PermissionRule[] | undefined
): boolean =>
  getOrPermissionGroups(rules).some((group) => {
    if (group.length <= 1 || !group.includes(slugToRemove)) return false;
    const selectedInGroup = group.filter((permission) =>
      selectedPermissions.includes(permission)
    );
    return selectedInGroup.length === 1 && selectedInGroup[0] === slugToRemove;
  });

const isDependencyRuleType = (rule: PermissionRuleType): boolean =>
  rule === PermissionRuleType.DEPENDENCY_AND ||
  rule === PermissionRuleType.DEPENDENCY_OR;

export const getDependencyPermissionRules = (
  rules: PermissionRule[] | undefined
): PermissionRule[] =>
  (rules ?? []).filter(
    (item) =>
      isDependencyRuleType(item.rule) &&
      item.permissions.length > 0 &&
      (item.targetPermissions?.length ?? 0) > 0
  );

const hasSelectedChild = (
  selectedPermissions: string[],
  childPermissions: string[]
): boolean =>
  childPermissions.some((permission) =>
    selectedPermissions.includes(permission)
  );

const getSelectedTargets = (
  selectedPermissions: string[],
  targetPermissions: string[]
): string[] =>
  targetPermissions.filter((permission) =>
    selectedPermissions.includes(permission)
  );

/** Targets that cannot be unchecked while a dependent child stays selected. */
export const getDependencyLockedPermissionSlugs = (
  selectedPermissions: string[],
  rules: PermissionRule[] | undefined
): string[] => {
  const locked = new Set<string>();

  getDependencyPermissionRules(rules).forEach((rule) => {
    if (!hasSelectedChild(selectedPermissions, rule.permissions)) return;
    const targets = rule.targetPermissions ?? [];

    if (rule.rule === PermissionRuleType.DEPENDENCY_AND) {
      targets.forEach((target) => locked.add(target));
      return;
    }

    const selectedTargets = getSelectedTargets(selectedPermissions, targets);
    if (selectedTargets.length === 1) {
      locked.add(selectedTargets[0]);
    }
  });

  return [...locked];
};

const formatQuotedPermissionLabels = (slugs: string[]): string => {
  const quotedLabels = slugs.map((slug, index) => {
    const { label } = getPermissionCatalogEntry(slug);
    const isLast = index === slugs.length - 1;
    return isLast ? `"${label}"` : `"${label},"`;
  });

  if (quotedLabels.length === 1) return quotedLabels[0];
  const leading = quotedLabels.slice(0, -1).join(' ');
  return `${leading} or ${quotedLabels[quotedLabels.length - 1]}`;
};

export const formatDependencyAndStayEnabledTooltip = (
  targetSlug: string,
  childSlugs: string[]
): string => {
  if (!targetSlug || !childSlugs.length) return '';
  const { label: targetLabel } = getPermissionCatalogEntry(targetSlug);
  return `"${targetLabel}" is required for ${formatQuotedPermissionLabels(
    childSlugs
  )}.`;
};

export const formatDependencyOrStayEnabledTooltip = (
  targetSlugs: string[],
  childSlugs: string[]
): string => {
  if (!targetSlugs.length || !childSlugs.length) return '';
  return `At least one of ${formatQuotedPermissionLabels(
    targetSlugs
  )} must stay enabled for ${formatQuotedPermissionLabels(childSlugs)}.`;
};

/** Tooltip copy for dependency-locked target permission slugs. */
export const getDependencyPermissionTooltipBySlug = (
  selectedPermissions: string[],
  rules: PermissionRule[] | undefined
): Record<string, string> => {
  const tooltips: Record<string, string> = {};

  getDependencyPermissionRules(rules).forEach((rule) => {
    const selectedChildren = rule.permissions.filter((permission) =>
      selectedPermissions.includes(permission)
    );
    if (!selectedChildren.length) return;

    const targets = rule.targetPermissions ?? [];

    if (rule.rule === PermissionRuleType.DEPENDENCY_AND) {
      targets.forEach((target) => {
        if (!selectedPermissions.includes(target)) return;
        tooltips[target] = formatDependencyAndStayEnabledTooltip(
          target,
          selectedChildren
        );
      });
      return;
    }

    const selectedTargets = getSelectedTargets(selectedPermissions, targets);
    if (selectedTargets.length !== 1) return;
    tooltips[selectedTargets[0]] = formatDependencyOrStayEnabledTooltip(
      targets,
      selectedChildren
    );
  });

  return tooltips;
};

/**
 * After a selection change, ensure dependency targets stay satisfied.
 * AND: add all targets when any child is selected.
 * OR: if a child is selected and no target remains, restore one.
 */
export const applyDependencyPermissionSelections = (
  nextSelected: string[],
  previousSelected: string[],
  rules: PermissionRule[] | undefined,
  assignablePermissions: string[] = []
): string[] => {
  const result = new Set(nextSelected);
  const canAdd = (slug: string) =>
    assignablePermissions.length === 0 || assignablePermissions.includes(slug);

  getDependencyPermissionRules(rules).forEach((rule) => {
    if (!hasSelectedChild([...result], rule.permissions)) return;
    const targets = rule.targetPermissions ?? [];

    if (rule.rule === PermissionRuleType.DEPENDENCY_AND) {
      targets.forEach((target) => {
        if (canAdd(target)) result.add(target);
      });
      return;
    }

    const hasTarget = targets.some((target) => result.has(target));
    if (hasTarget) return;

    const previouslySelected = [...targets]
      .reverse()
      .find((target) => previousSelected.includes(target) && canAdd(target));
    const fallback = targets.find((target) => canAdd(target));
    const restored = previouslySelected ?? fallback;
    if (restored) result.add(restored);
  });

  return [...result];
};

/**
 * True only when removing `slugToRemove` drops a still-required dependency
 * target. Aligns with getDependencyLockedPermissionSlugs so an already
 * unsatisfied dependency (e.g. missing/non-assignable target) does not
 * silently block unchecking unrelated permissions.
 */
export const wouldViolateDependencyPermissionRule = (
  selectedPermissions: string[],
  slugToRemove: string,
  rules: PermissionRule[] | undefined
): boolean => {
  const nextSelected = selectedPermissions.filter(
    (permission) => permission !== slugToRemove
  );

  return getDependencyPermissionRules(rules).some((rule) => {
    // Unchecking the last child clears the dependency — always allowed.
    if (!hasSelectedChild(nextSelected, rule.permissions)) return false;
    const targets = rule.targetPermissions ?? [];

    if (rule.rule === PermissionRuleType.DEPENDENCY_AND) {
      return targets.includes(slugToRemove);
    }

    const selectedTargets = getSelectedTargets(selectedPermissions, targets);
    return selectedTargets.length === 1 && selectedTargets[0] === slugToRemove;
  });
};

export const getUnsatisfiedDependencyPermissionRules = (
  selectedPermissions: string[],
  rules: PermissionRule[] | undefined,
  assignablePermissions?: string[]
): PermissionRule[] =>
  getDependencyPermissionRules(rules).filter((rule) => {
    if (!hasSelectedChild(selectedPermissions, rule.permissions)) return false;
    const targets = rule.targetPermissions ?? [];
    const relevantTargets =
      assignablePermissions && assignablePermissions.length > 0
        ? targets.filter((target) => assignablePermissions.includes(target))
        : targets;

    // No grantable targets — user cannot satisfy this rule; don't block Create.
    if (relevantTargets.length === 0) return false;

    if (rule.rule === PermissionRuleType.DEPENDENCY_AND) {
      return relevantTargets.some(
        (target) => !selectedPermissions.includes(target)
      );
    }

    return !relevantTargets.some((target) =>
      selectedPermissions.includes(target)
    );
  });

export const isCreateCustomRoleFormValid = (
  roleName: string,
  baseRoleId: number | null,
  selectedPermissions: string[],
  roleNameError?: string,
  nameMinLength = CUSTOM_ROLE_NAME_MIN_LENGTH,
  permissionRules?: PermissionRule[],
  nameMaxLength = CUSTOM_ROLE_NAME_MAX_LENGTH,
  assignablePermissions?: string[],
  visiblePermissions?: string[]
): boolean => {
  const trimmedName = roleName.trim();
  const hasVisiblePermissionSelected = visiblePermissions
    ? selectedPermissions.some((slug) => visiblePermissions.includes(slug))
    : selectedPermissions.length > 0;

  return (
    !roleNameError &&
    trimmedName.length >= nameMinLength &&
    trimmedName.length <= nameMaxLength &&
    baseRoleId !== null &&
    hasVisiblePermissionSelected &&
    !getUnsatisfiedMultiOrPermissionGroups(
      selectedPermissions,
      permissionRules,
      assignablePermissions
    ).length &&
    !getUnsatisfiedDependencyPermissionRules(
      selectedPermissions,
      permissionRules,
      assignablePermissions
    ).length
  );
};

/** System (non-custom) role for a given role_scope, if present. */
export const findScopeBaseRole = (
  roleScope: RoleScope,
  roles: OrgRoleMeta[]
): OrgRoleMeta | undefined =>
  roles.find((role) => role.role_scope === roleScope && !role.isCustom);

/**
 * Permissions listed in the view-permissions panel.
 * Custom roles show the full scope base set; system roles show their own.
 */
export const getViewPermissionSlugs = (
  selectedRole: OrgRoleMeta,
  roles: OrgRoleMeta[] = []
): string[] => {
  if (!selectedRole.isCustom) {
    return selectedRole.permissions;
  }

  const scopeBaseRole = findScopeBaseRole(selectedRole.role_scope, roles);
  return scopeBaseRole?.permissions ?? selectedRole.permissions;
};

/** Role whose permission_rules apply when viewing permissions. */
export const getViewPermissionRulesRole = (
  selectedRole: OrgRoleMeta,
  roles: OrgRoleMeta[] = []
): OrgRoleMeta => {
  if (!selectedRole.isCustom) return selectedRole;
  return findScopeBaseRole(selectedRole.role_scope, roles) ?? selectedRole;
};

export const mergeCreateCustomRolePermissions = (
  selectedPermissions: string[],
  requiredPermissions: string[]
): string[] => [...new Set([...selectedPermissions, ...requiredPermissions])];

export const buildCreateCustomRolePermissionsPayload = (
  selectedPermissions: string[],
  baseRole: OrgRoleMeta,
  assignablePermissions: string[],
  featureContext: CreateRoleFeatureContext
): string[] => {
  const hiddenPermissions = getHiddenCreateFlowPermissions(
    baseRole,
    assignablePermissions,
    featureContext
  );
  // API HIDE rules mean "hidden in UI but still granted" — they win over the
  // static omit list, which otherwise drops the slug from the payload.
  const hideRuleSlugs = getHideRuleSlugs(baseRole.permissionRules);
  const merged = stripOmittedCustomRolePermissions(
    stripLegacyPaymentOrderEdit(
      mergeCreateCustomRolePermissions(selectedPermissions, hiddenPermissions)
    ),
    hideRuleSlugs
  );

  // Legacy: send payment_order_edit only when every split payment action
  // permission is granted, so older app builds that still gate on edit keep
  // working. Safe to remove once those clients are retired.
  if (shouldIncludeLegacyPaymentOrderEdit(merged, assignablePermissions)) {
    return [...merged, UserPermissionTypes.PAYMENT_ORDER_EDIT];
  }
  return merged;
};

/** Roles the user may clone when creating a custom role. */
export const getExtendableRoles = (roles: OrgRoleMeta[]): OrgRoleMeta[] =>
  roles.filter((role) => role.allowExtendingRole === true);

/** Hide feature-gated roles (LMS, events, attendance) when the org flag is off. */
export const isRoleAvailableForOrgFeatures = (
  role: OrgRoleMeta,
  context: CreateRoleFeatureContext
): boolean => {
  switch (role.role_scope) {
    case RoleScope.SALES_AGENT:
    case RoleScope.SALES_MANAGER:
      return (
        context.orgFeatureFlags?.includes(FEATURE_FLAGS.LEAD_MANAGEMENT) ??
        false
      );
    case RoleScope.EVENT_MANAGER:
    case RoleScope.EVENT_VOLUNTEER:
      return context.orgFeatureFlags?.includes(FEATURE_FLAGS.EVENTS) ?? false;
    case RoleScope.ATTENDANCE_MANAGER:
      return Boolean(context.isAttendanceFeatureEnabled);
    default:
      return true;
  }
};

export const filterRolesByOrgFeatures = (
  roles: OrgRoleMeta[],
  context: CreateRoleFeatureContext
): OrgRoleMeta[] =>
  roles.filter((role) => isRoleAvailableForOrgFeatures(role, context));

export const canCreateCustomRolesFromRoles = (
  canCreateCustomRoles: boolean,
  roles: OrgRoleMeta[]
): boolean => canCreateCustomRoles && getExtendableRoles(roles).length > 0;

export const includeCurrentRoleInRoles = (
  roles: OrgRoleMeta[],
  currentRole: OrgRoleMeta | null
): OrgRoleMeta[] => {
  if (!currentRole) return roles;
  const isCurrentRoleInList = roles.some((role) => role.id === currentRole.id);
  return isCurrentRoleInList ? roles : [currentRole, ...roles];
};

export const mapSelectedGroupsToGroupsByBranch = (
  selectedGroups: SelectedItemsObj
): Record<string, string[]> =>
  Object.fromEntries(
    Object.entries(selectedGroups)
      .filter(([, groups]) => groups.length > 0)
      .map(([branchId, groups]) => [branchId, groups.map((group) => group.id)])
  );

const buildBranchPayload = (branchIds: string[]): InviteBranchPayload[] =>
  branchIds.map((branch_id) => ({ branch_id }));

const buildGroupPayload = (
  groupsByBranch: Record<string, string[]>
): InviteGroupPayload[] =>
  Object.entries(groupsByBranch)
    .filter(([, groupIds]) => groupIds.length > 0)
    .map(([branch_id, group_ids]) => ({ branch_id, group_ids }));

/** Custom roles with organisation_admin scope (not the system org admin). */
export const isCustomOrganisationAdminRole = (
  role: OrgRoleMeta | null | undefined
): boolean =>
  Boolean(role?.isCustom && role.role_scope === RoleScope.ORGANISATION_ADMIN);

const ORG_WIDE_ONLY_PERMISSIONS: string[] = [
  UserPermissionTypes.BRANCH_CREATE,
  UserPermissionTypes.GROUP_CREATE
];

/**
 * A custom organisation_admin role that can create branches or groups cannot be
 * limited to a group subset, so there is no access level to choose.
 */
export const requiresOrgWideAccess = (
  role: OrgRoleMeta | null | undefined
): boolean =>
  isCustomOrganisationAdminRole(role) &&
  (role?.permissions ?? []).some((slug) =>
    ORG_WIDE_ONLY_PERMISSIONS.includes(slug)
  );

export const buildScopeBranchesPayload = (
  scope: RoleScope,
  formState: Pick<
    MemberFormState,
    'branchIds' | 'groupsByBranch' | 'selectedRole' | 'orgAccessMode'
  >
): InviteBranchPayload[] | InviteGroupPayload[] | undefined => {
  if (isCustomOrganisationAdminRole(formState.selectedRole)) {
    if (formState.orgAccessMode === 'all_branches') return undefined;
    return buildGroupPayload(formState.groupsByBranch);
  }

  const variant = getScopeUiVariant(scope);
  if (variant === 'org_info' || variant === 'none') return undefined;
  if (variant === 'branch_multi_select') {
    return buildBranchPayload(formState.branchIds);
  }
  return buildGroupPayload(formState.groupsByBranch);
};

export const buildInviteMemberBody = (
  formState: MemberFormState
): InviteMemberBody | null => {
  if (!formState.selectedRole) return null;

  const { selectedRole, name, phoneNumber } = formState;
  const branches = buildScopeBranchesPayload(
    selectedRole.role_scope,
    formState
  );

  return {
    mobile: `91${phoneNumber}`,
    name,
    role: selectedRole.name,
    ...(selectedRole.isCustom && { is_custom_role: true }),
    ...(branches && { branches })
  };
};

export const buildUpdateMemberRoleBody = (
  formState: MemberFormState,
  userId: string
): UpdateMemberRoleBody | null => {
  if (!formState.selectedRole) return null;

  const branches = buildScopeBranchesPayload(
    formState.selectedRole.role_scope,
    formState
  );

  return {
    user_id: userId,
    role_name: formState.selectedRole.name,
    ...(formState.selectedRole.isCustom && { is_custom_role: true }),
    ...(branches && { branches })
  };
};

export const hasRoleChanged = (
  currentRoleName: string | undefined,
  selectedRole: OrgRoleMeta | null
): boolean => {
  if (!selectedRole || !currentRoleName) return false;
  return currentRoleName !== selectedRole.name;
};

/** Built-in scopes whose entire purpose is working leads. */
const LEAD_MANAGEMENT_ROLE_SCOPES: RoleScope[] = [
  RoleScope.SALES_AGENT,
  RoleScope.SALES_MANAGER
];

/**
 * Scope-only check for call sites that only have a member's `role_scope`
 * (e.g. the org members table row, or a user's current role before a change).
 */
export const hasLeadManagementRoleScope = (roleScope: RoleScope): boolean =>
  LEAD_MANAGEMENT_ROLE_SCOPES.includes(roleScope);

/** Reads `rows` from either `{ rows }` or a `{ data: { rows } }` envelope. */
export const countAgentLeads = (payload: unknown): number => {
  if (!payload || typeof payload !== 'object') return 0;

  const body =
    'data' in payload ? (payload as { data?: unknown }).data : payload;
  const rows = (body as { rows?: unknown })?.rows;

  return Array.isArray(rows) ? rows.length : 0;
};

export const buildLeadReassignDescription = (
  count: number,
  actionDescription: string = 'Removing Lead Management access'
): string =>
  `This user has ${count} open ${pluralize(count, 'lead')} assigned to them. ` +
  `${actionDescription} will make ${pluralize(
    count,
    'this lead',
    'these leads'
  )} unassigned. Converted leads will stay as they are.` +
  `\n\nDo you want to proceed?`;

/** Display label for roles — known map first, else snake_case → Title Case. */
export const formatRoleDisplayName = (roleName: string): string => {
  if (!roleName) return '';
  const mapped = roleDisplayNameMap[roleName];
  if (mapped) return mapped;
  if (roleName.includes('_')) {
    return capitalizeWords(roleName.split('_')).join(' ');
  }
  if (roleName === roleName.toLowerCase() && !roleName.includes(' ')) {
    return capitalizeFirstLetter(roleName);
  }
  return roleName;
};

export const getRoleDisplayName = (
  role: Pick<OrgRoleMeta, 'name' | 'displayName'> | null | undefined
): string =>
  role?.displayName?.trim() || formatRoleDisplayName(role?.name ?? '');

export const mapRoleToDropdownOption = (role: OrgRoleMeta) => ({
  id: role.id,
  label: getRoleDisplayName(role),
  value: String(role.id),
  isCustom: Boolean(role.isCustom)
});

export const findRoleById = (
  roles: OrgRoleMeta[],
  roleId: number | string | null
): OrgRoleMeta | null => {
  if (roleId === null || roleId === undefined || roleId === '') return null;
  const id = typeof roleId === 'string' ? Number(roleId) : roleId;
  return roles.find((role) => role.id === id) ?? null;
};

export const findRoleByName = (
  roles: OrgRoleMeta[],
  roleName: string | undefined | null
): OrgRoleMeta | null => {
  if (!roleName) return null;
  return (
    roles.find(
      (role) => role.name.toLowerCase() === roleName.trim().toLowerCase()
    ) ?? null
  );
};

type ApiPermissionRule = {
  permissions?: string[];
  target_permissions?: string[];
  targetPermissions?: string[];
  rule?: string;
};

type ApiOrgRole = {
  id: number;
  name: string;
  display_name?: string;
  displayName?: string;
  role_scope?: RoleScope;
  /** List API field; normalized to role_scope. */
  scope?: RoleScope;
  permissions?: string[];
  isCustom?: boolean;
  is_custom?: boolean;
  creationPermission?: string;
  creation_permission?: string;
  allowExtendingRole?: boolean;
  allow_extending_role?: boolean;
  permissionRules?: ApiPermissionRule[];
  permission_rules?: ApiPermissionRule[];
};

const VALID_PERMISSION_RULES = new Set<string>(
  Object.values(PermissionRuleType)
);

const normalizeTargetPermissions = (
  rule: ApiPermissionRule
): string[] | undefined => {
  const targets = rule.targetPermissions ?? rule.target_permissions;
  if (!Array.isArray(targets)) return undefined;
  return targets;
};

export const normalizePermissionRules = (
  rules: ApiPermissionRule[] | undefined
): PermissionRule[] =>
  (rules ?? [])
    .filter(
      (rule): rule is ApiPermissionRule & { rule: PermissionRuleType } => {
        if (!rule?.rule || !VALID_PERMISSION_RULES.has(rule.rule)) return false;
        if (!Array.isArray(rule.permissions)) return false;
        if (
          rule.rule === PermissionRuleType.DEPENDENCY_AND ||
          rule.rule === PermissionRuleType.DEPENDENCY_OR
        ) {
          return Array.isArray(
            rule.targetPermissions ?? rule.target_permissions
          );
        }
        return true;
      }
    )
    .map((rule) => {
      const targetPermissions = normalizeTargetPermissions(rule);
      return {
        rule: rule.rule,
        permissions: rule.permissions ?? [],
        ...(targetPermissions ? { targetPermissions } : {})
      };
    });

export const normalizeOrgRole = (role: ApiOrgRole): OrgRoleMeta => ({
  id: role.id,
  name: role.name,
  displayName: role.displayName ?? role.display_name,
  role_scope: (role.role_scope ?? role.scope) as RoleScope,
  permissions: role.permissions ?? [],
  isCustom: role.isCustom ?? role.is_custom,
  creationPermission: role.creationPermission ?? role.creation_permission,
  allowExtendingRole: role.allowExtendingRole ?? role.allow_extending_role,
  permissionRules: normalizePermissionRules(
    role.permissionRules ?? role.permission_rules
  )
});

type CustomRolesApiPayload = {
  roles?: ApiOrgRole[];
  currentUserPermissions?: string[];
  current_user_permissions?: string[];
  canCreateCustomRoles?: boolean;
  can_create_custom_roles?: boolean;
};

/** Unwraps `{ data: T }` envelopes from API responses. */
export const unwrapApiDataPayload = <T extends object>(
  response: T | { data?: T }
): T => {
  if (
    'data' in response &&
    response.data &&
    typeof response.data === 'object'
  ) {
    return response.data;
  }
  return response as T;
};

/** Accepts either `{ roles }` or `{ data: { roles } }` from the API. */
export const unwrapCustomRolesPayload = (
  response:
    | CustomRolesApiPayload
    | { data?: CustomRolesApiPayload }
    | null
    | undefined
): CustomRolesApiPayload => {
  if (!response || typeof response !== 'object') return {};
  if ('roles' in response && Array.isArray(response.roles)) {
    return response;
  }
  if (
    'data' in response &&
    response.data &&
    typeof response.data === 'object'
  ) {
    return response.data;
  }
  return {};
};

export const normalizeCustomRolesResponse = (
  response:
    | CustomRolesApiPayload
    | { data?: CustomRolesApiPayload }
    | null
    | undefined
): GetCustomRolesResponse => {
  const payload = unwrapCustomRolesPayload(response);
  return {
    roles: (payload.roles ?? []).map(normalizeOrgRole),
    currentUserPermissions:
      payload.currentUserPermissions ?? payload.current_user_permissions ?? [],
    canCreateCustomRoles:
      payload.canCreateCustomRoles ?? payload.can_create_custom_roles ?? false
  };
};

export const isScopeAssignmentValid = (
  scope: RoleScope | undefined,
  formState: Pick<
    MemberFormState,
    'branchIds' | 'groupsByBranch' | 'selectedRole' | 'orgAccessMode'
  >
): boolean => {
  if (!scope) return false;

  if (isCustomOrganisationAdminRole(formState.selectedRole)) {
    if (formState.orgAccessMode === 'all_branches') return true;
    return Object.values(formState.groupsByBranch).some(
      (groupIds) => groupIds.length > 0
    );
  }

  const variant = getScopeUiVariant(scope);
  if (variant === 'org_info' || variant === 'none') return true;
  if (variant === 'branch_multi_select') {
    return formState.branchIds.length > 0;
  }
  if (variant === 'group_picker') {
    return Object.values(formState.groupsByBranch).some(
      (groupIds) => groupIds.length > 0
    );
  }
  return true;
};

export const isInviteMemberFormValid = (
  formState: MemberFormState,
  nameMaxLength: number,
  nameMinLength: number
): boolean => {
  if (!formState.selectedRole) return false;
  if (
    isNameAndPhoneValid(
      formState.name,
      formState.phoneNumber,
      String(nameMaxLength),
      String(nameMinLength)
    )
  ) {
    return false;
  }
  return isScopeAssignmentValid(formState.selectedRole.role_scope, formState);
};
